import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";

// === Reliability indicator (green / yellow / red) ===
const RELIABILITY = {
  high: { dot: "bg-emerald-500", label: "Affidabilità: alta", text: "text-emerald-700" },
  medium: { dot: "bg-amber-500", label: "Affidabilità: media", text: "text-amber-700" },
  low: { dot: "bg-rose-500", label: "Affidabilità: bassa", text: "text-rose-700" },
};

// === 2026-02-16 (Laura): 5 categorie tematiche ===
// Definizione delle sezioni con header, emoji, sottotitolo e classe colore.
// 2026-02-19 (Laura): titoli più d'impatto — banner con border-l forte +
// emoji grande + Cormorant ingrandito + chip count, più aria intorno alle card.
const SECTIONS = [
  {
    key: "important",
    emoji: "🌟",
    title: "Le scoperte più importanti",
    subtitle: "Le cose che spiegano di più la tua vita.",
    accent: "from-amber-50 to-yellow-50/40",
    border: "border-amber-300/60",
    borderL: "border-l-amber-500",
  },
  {
    key: "resources",
    emoji: "💎",
    title: "Risorse da coltivare",
    subtitle: "Quello che ti fa bene — e che vale la pena alimentare.",
    accent: "from-emerald-50 to-teal-50/30",
    border: "border-emerald-300/50",
    borderL: "border-l-emerald-500",
  },
  {
    key: "to_observe",
    emoji: "⚠️",
    title: "Aree da osservare",
    subtitle: "Le cose che — sui dati — sembrano abbassare il tuo benessere.",
    accent: "from-rose-50 to-orange-50/30",
    border: "border-rose-300/50",
    borderL: "border-l-rose-500",
  },
  {
    key: "variable",
    emoji: "🎢",
    title: "Situazioni variabili",
    subtitle: "Esperienze imprevedibili: a volte alto, a volte basso.",
    accent: "from-sky-50 to-indigo-50/30",
    border: "border-sky-300/50",
    borderL: "border-l-sky-500",
  },
  {
    // 2026-02-19 (Laura): nuova sezione dedicata alle insight "two_faces"
    // — i fattori contestuali che cambiano radicalmente come vivi la stessa
    // esperienza (smart working vs presenza, scelta vs imposizione, ecc.).
    key: "context_shifts",
    emoji: "🔀",
    title: "Dove cambia tutto",
    subtitle: "I fattori contestuali che cambiano radicalmente come vivi la stessa esperienza.",
    accent: "from-indigo-50 to-purple-50/30",
    border: "border-indigo-300/50",
    borderL: "border-l-indigo-500",
  },
  {
    key: "surprising",
    emoji: "🤔",
    title: "Scoperte sorprendenti",
    subtitle: "Pattern che meritano una seconda lettura.",
    accent: "from-violet-50 to-fuchsia-50/30",
    border: "border-violet-300/50",
    borderL: "border-l-violet-500",
  },
];

const InsightCard = ({ insight, testid }) => {
  const rel = RELIABILITY[insight.reliability] || RELIABILITY.low;
  return (
    <Card
      className="border bg-gradient-to-br from-white via-stone-50 to-amber-50/30 shadow-sm hover:shadow-md transition-shadow"
      data-testid={testid}
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start gap-3">
          <div className="text-2xl shrink-0" aria-hidden="true">{insight.emoji}</div>
          <h4
            className="text-sm sm:text-base font-semibold leading-snug text-stone-800 flex-1"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
          >
            {insight.title}
          </h4>
        </div>
        <p className="text-xs sm:text-sm leading-relaxed text-stone-600">
          {insight.body}
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 border-t border-stone-200/60">
          <span className="text-[11px] font-medium text-stone-700 tabular-nums">
            {insight.metric}
          </span>
          <span className="flex items-center gap-1.5 text-[11px]">
            <span className={`inline-block w-2 h-2 rounded-full ${rel.dot}`} />
            <span className={rel.text}>{rel.label}</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const ThemeSection = ({ section, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-3" data-testid={`wrapped-section-${section.key}`}>
      <div
        className={`rounded-xl border ${section.border} border-l-4 ${section.borderL} bg-gradient-to-r ${section.accent} px-4 py-3.5 shadow-sm`}
      >
        <div className="flex items-start gap-3">
          <span className="text-3xl leading-none shrink-0 mt-0.5" aria-hidden="true">
            {section.emoji}
          </span>
          <div className="flex-1 min-w-0">
            <h4
              className="text-lg sm:text-xl font-semibold text-stone-800 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              {section.title}
            </h4>
            <p className="text-[11px] sm:text-xs text-stone-600 leading-snug mt-1">
              {section.subtitle}
            </p>
          </div>
          <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-white/90 text-xs font-semibold text-stone-700 tabular-nums shrink-0 shadow-sm">
            {items.length}
          </span>
        </div>
      </div>
      <div className="space-y-2.5 pl-1">
        {items.map((ins, idx) => (
          <InsightCard
            key={`${ins.kind}-${idx}`}
            insight={ins}
            testid={`wrapped-card-${section.key}-${ins.kind}-${idx}`}
          />
        ))}
      </div>
    </div>
  );
};

const EmptyState = ({ minObs }) => (
  <Card className="border-dashed border-stone-300">
    <CardContent className="py-6 px-4 text-center space-y-1.5">
      <Sparkles className="w-6 h-6 text-stone-300 mx-auto" />
      <p className="text-sm font-medium text-stone-700">
        Le tue scoperte stanno arrivando
      </p>
      <p className="text-[11px] text-stone-500 leading-relaxed max-w-sm mx-auto">
        Per iniziare a notare i tuoi pattern serve qualche check-in in più
        (almeno {minObs} per ogni categoria). Continua così e qui apparirà ciò che ti caratterizza.
      </p>
    </CardContent>
  </Card>
);

// === 2026-02-21 (Laura): EVENT-CENTRIC banners ===
// Due sezioni dedicate ai fattori che accomunano i momenti estremi.
// Sostituiscono progressivamente l'approccio variable-centric per evitare
// tautologie ("stai meglio quando l'umore è alto") e dare un'analisi
// multifattoriale, più vicina alla case-formulation clinica.
const EVENT_BEST = {
  key: "best_moments_factors",
  emoji: "✨",
  title: "Cosa accomuna i tuoi momenti migliori",
  subtitle:
    "Fattori che ricorrono nel tuo 15% di check-in con umore più alto, molto più del solito.",
  accent: "from-emerald-50 via-amber-50 to-teal-50/30",
  border: "border-emerald-300/60",
  borderL: "border-l-emerald-500",
};
const EVENT_WORST = {
  key: "worst_moments_factors",
  emoji: "🌫️",
  title: "Cosa accomuna i tuoi momenti più difficili",
  subtitle:
    "Fattori che ricorrono nel tuo 15% di check-in con umore più basso, molto più del solito.",
  accent: "from-rose-50 via-orange-50 to-amber-50/30",
  border: "border-rose-300/60",
  borderL: "border-l-rose-500",
};

const EventCentricBlurb = ({ stats }) => {
  if (!stats || !stats.active) return null;
  return (
    <p className="text-[11px] text-stone-500 leading-relaxed px-1">
      Confronto tra i tuoi {stats.n_best || 0} check-in con umore più alto e i tuoi{" "}
      {stats.n_worst || 0} con umore più basso (top/bottom {Math.round((stats.pct || 0.15) * 100)}%) rispetto alla tua
      baseline. Mostrate solo le ricorrenze con almeno {stats.min_extreme_n || 5} occorrenze
      e frequenza ≥ {(stats.min_lift || 1.5).toString().replace(".", ",")}× rispetto al baseline.
    </p>
  );
};

// === 2026-02-21 (Laura): WORK × LOCATION banner ===
// Card dedicata che disambigua i casi tipici (smart working vs presenza vs
// lavoro raro). Mostrata SOLO se il backend restituisce un'insight non nulla.
const WORK_LOC = {
  key: "work_location_insight",
  emoji: "💼",
  title: "Come vivi il lavoro",
  subtitle:
    "Cosa cambia tra ufficio, smart working e altri luoghi — nei tuoi check-in di lavoro.",
  accent: "from-amber-50 via-stone-50 to-emerald-50/30",
  border: "border-amber-300/60",
  borderL: "border-l-amber-500",
};

const WorkLocationSection = ({ insight }) => {
  if (!insight) return null;
  const rel = RELIABILITY[insight.reliability] || RELIABILITY.low;
  return (
    <div className="space-y-3" data-testid="wrapped-section-work_location_insight">
      <div
        className={`rounded-xl border ${WORK_LOC.border} border-l-4 ${WORK_LOC.borderL} bg-gradient-to-r ${WORK_LOC.accent} px-4 py-3.5 shadow-sm`}
      >
        <div className="flex items-start gap-3">
          <span className="text-3xl leading-none shrink-0 mt-0.5" aria-hidden="true">
            {WORK_LOC.emoji}
          </span>
          <div className="flex-1 min-w-0">
            <h4
              className="text-lg sm:text-xl font-semibold text-stone-800 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              {WORK_LOC.title}
            </h4>
            <p className="text-[11px] sm:text-xs text-stone-600 leading-snug mt-1">
              {WORK_LOC.subtitle}
            </p>
          </div>
        </div>
      </div>
      <div className="pl-1">
        <Card
          className="border bg-gradient-to-br from-white via-stone-50 to-amber-50/30 shadow-sm hover:shadow-md transition-shadow"
          data-testid={`wrapped-card-work_location-${insight.kind}`}
        >
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start gap-3">
              <div className="text-2xl shrink-0" aria-hidden="true">{insight.emoji}</div>
              <h4
                className="text-sm sm:text-base font-semibold leading-snug text-stone-800 flex-1"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
              >
                {insight.title}
              </h4>
            </div>
            <p className="text-xs sm:text-sm leading-relaxed text-stone-600">
              {insight.body}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 border-t border-stone-200/60">
              <span className="text-[11px] font-medium text-stone-700 tabular-nums">
                {insight.metric}
              </span>
              <span className="flex items-center gap-1.5 text-[11px]">
                <span className={`inline-block w-2 h-2 rounded-full ${rel.dot}`} />
                <span className={rel.text}>{rel.label}</span>
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const WrappedInsightsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/voice-mood/wrapped-insights?days=365")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="wrapped-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  // 2026-02-16 (Laura): nuova struttura a 5 categorie tematiche.
  // Fallback su essential+advanced se backend non ha ancora i nuovi campi.
  const themed = SECTIONS.map((s) => ({
    section: s,
    items: data[s.key] || [],
  }));
  const totalThemed = themed.reduce((acc, t) => acc + t.items.length, 0);

  // 2026-02-21 (Laura): event-centric — cosa accomuna i momenti migliori/peggiori.
  const bestMoments = data.best_moments_factors || [];
  const worstMoments = data.worst_moments_factors || [];
  const eventStats = data.event_centric_stats || {};
  const hasEventCentric = bestMoments.length > 0 || worstMoments.length > 0;

  // 2026-02-21 (Laura): work × location — smart vs presenza vs altro.
  const workLocationInsight = data.work_location_insight || null;
  const hasWorkLoc = !!workLocationInsight;

  // Fallback legacy: se non ci sono campi tematici, mostro essential+advanced come prima
  const legacy = [...(data.essential || []), ...(data.advanced || [])];
  const hasAny = totalThemed > 0 || legacy.length > 0 || hasEventCentric || hasWorkLoc;

  return (
    <section className="space-y-3" data-testid="wrapped-insights-section">
      <div className="pb-1">
        <h3
          className="text-xl sm:text-2xl font-semibold text-stone-800 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Cose che abbiamo scoperto su di te
        </h3>
        <p className="text-xs text-stone-500 mt-0.5">
          Una lettura narrativa dei tuoi check-in, organizzata per temi.
        </p>
      </div>

      {!hasAny ? (
        <EmptyState minObs={data.min_observations} />
      ) : totalThemed > 0 || hasEventCentric || hasWorkLoc ? (
        <div className="space-y-6">
          {/* 2026-02-21 (Laura): event-centric sections in cima — accomunano */}
          {/* i fattori che caratterizzano i momenti realmente migliori/peggiori. */}
          {hasEventCentric && (
            <div className="space-y-4">
              <ThemeSection section={EVENT_BEST} items={bestMoments} />
              <ThemeSection section={EVENT_WORST} items={worstMoments} />
              <EventCentricBlurb stats={eventStats} />
            </div>
          )}
          {/* 2026-02-21 (Laura): card dedicata "Come vivi il lavoro" — */}
          {/* disambigua smart working vs presenza per attività=lavoro. */}
          {hasWorkLoc && <WorkLocationSection insight={workLocationInsight} />}
          {themed.map(({ section, items }) => (
            <ThemeSection key={section.key} section={section} items={items} />
          ))}
        </div>
      ) : (
        // Fallback legacy
        <div className="space-y-2.5" data-testid="wrapped-essential-list">
          {legacy.map((ins, idx) => (
            <InsightCard
              key={`${ins.kind}-${idx}`}
              insight={ins}
              testid={`wrapped-card-${ins.kind}-${idx}`}
            />
          ))}
        </div>
      )}

      {hasAny && (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-[10px] text-stone-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
            ≥20 dati
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
            5–19 dati
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500" />
            &lt;5 dati
          </span>
        </div>
      )}
    </section>
  );
};

export default WrappedInsightsSection;
